import { useEffect } from "react";
import { SITE_URL } from "@/lib/siteConfig";

interface SeoHeadProps {
  title: string;
  description: string;
  canonical: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  jsonLd?: Record<string, unknown>;
}

/**
 * SeoHead — lightweight <head> manager for the SPA.
 *
 * Sets document.title, injects/updates <meta>, <link rel="canonical">,
 * and optional JSON-LD structured data on mount, and cleans up on unmount.
 *
 * Usage:
 *   <SeoHead
 *     title="AI Email Campaign Generator — MoreMail"
 *     description="Generate complete multi-step email campaigns with AI."
 *     canonical={`${SITE_URL}/ai-campaign-generator`}
 *   />
 */
export default function SeoHead({
  title,
  description,
  canonical,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = "website",
  twitterCard = "summary_large_image",
  jsonLd,
}: SeoHeadProps) {
  useEffect(() => {
    // ── Title ──
    const prevTitle = document.title;
    document.title = title;

    // ── Meta helper ──
    const metas: HTMLMetaElement[] = [];
    function setMeta(attr: string, key: string, content: string) {
      let el = document.querySelector<HTMLMetaElement>(
        `meta[${attr}="${key}"]`
      );
      const existed = !!el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      if (!existed) metas.push(el);
    }

    setMeta("name", "description", description);
    if (keywords) setMeta("name", "keywords", keywords);
    setMeta("name", "robots", "index, follow, max-snippet:-1, max-image-preview:large");

    // Open Graph
    setMeta("property", "og:title", ogTitle || title);
    setMeta("property", "og:description", ogDescription || description);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:url", canonical);
    if (ogImage) setMeta("property", "og:image", ogImage);

    // Twitter
    setMeta("name", "twitter:card", twitterCard);
    setMeta("name", "twitter:title", ogTitle || title);
    setMeta("name", "twitter:description", ogDescription || description);
    if (ogImage) setMeta("name", "twitter:image", ogImage);

    // ── Canonical ──
    let link = document.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );
    const linkExisted = !!link;
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = canonical;

    // ── JSON-LD Structured Data ──
    let script: HTMLScriptElement | null = null;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    // ── Cleanup ──
    return () => {
      document.title = prevTitle;
      metas.forEach((m) => m.remove());
      if (!linkExisted && link) link.remove();
      if (script) script.remove();
    };
  }, [title, description, canonical, keywords, ogTitle, ogDescription, ogImage, ogType, twitterCard, jsonLd]);

  return null; // renders nothing — side-effects only
}
